// Die Einträge des Benutzermenüs als eine Liste (SPEC VE-11), in jeder Menüform dieselben (AE-1 Nachtrag 3).
// Profil-Nachbau F-01 (Bennys Freigabe 14.10.2026, Vorbild meinGPT): drei Blöcke mit Trennlinie –
// 1 Meine Einstellungen, Hilfe und Feedback (seit PF-104 ein Link auf die Hilfe-Seite (F-06, F-19); bis dahin ein Untermenü mit
// Anleitungen, Fehler melden, Über, Rechtslinks und Datenschutzeinstellungen, die jetzt auf /hilfe/dokumente stehen);
// 2 (KI-Cockpit, bis F-04 „Nutzung im Team“), (Einstellungen der Firma), (Plattform-Verwaltung); 3 Abmelden.
// „Meine Nutzung“, „Alle Tarife anzeigen“ und „Mehr erfahren“ entfallen; ihre Ziele bleiben über Meine Einstellungen
// erreichbar. Die übrigen Ebenen, persönliche Bereiche und der Sichtschalter stehen im Einstellungen-Pop-up
// (pages/einstellungen/seiten.ts).
// Plan 1a Welle 2, Spur admin, Stück 1 (P3-01): der erste Eintrag heißt „Meine Einstellungen“ (EBENEN.meineEinstellungen),
// dasselbe Wort wie die Gruppe im Pop-up; das Pop-up selbst heißt weiter „Einstellungen“.
import { EBENEN, MENUE } from '../config/ebenen';
import type { Rechtslink } from '../config/legal';
import { EINSTELLUNGEN_PFAD, ORGANISATION_PFAD } from '../pages/einstellungen/seiten';
import { HILFE_PFAD } from './hilfe/hilfeLeiste';

export interface Ebenen {
  verwaltung?: { ziel: string; bereiche?: string[] } | null;
  // teamNutzung (Ä-20, UK-15): der Server sagt, ob das Konto in dieser Firma Personen ansehen darf (members.read).
  adminEinstellungen?: { ziel: string; firmaId: string; firmaName: string; teamNutzung?: boolean } | null;
  // kiCockpit (PF-105, F-04/F-08): Ziel des Eintrags „KI-Cockpit“, das Firmen-Cockpit; gesetzt genau mit teamNutzung.
  kiCockpit?: { ziel: string } | null;
  meineEinstellungen?: { ziel: string } | null;
  // meinTeam (PF-806, F-14): das eigene Team; nur dann steht „Mein Team“ in der Konto-Leiste (seiten.ts). In einer Sicht null.
  meinTeam?: { teamId: string; name: string; verwalten: boolean } | null;
}
// PF-104: die Aktion „fehler“ (Meldedialog aus dem Menü) entfällt; Anfragen stellt die Hilfe-Seite („Neue Anfrage“).
export type MenueAktion = 'abmelden';
export interface MenueEintrag {
  key: string;
  label: string;
  // F-01: der Block des Eintrags; zwischen zwei Blöcken steht eine Trennlinie (nur in der obersten Ebene).
  // 4: eigener Block der Plattform-Verwaltung zwischen 2 und 3 (F-23, PF-NB-verwaltung-6).
  block?: 1 | 2 | 3 | 4;
  // F-23 (PF-NB-verwaltung-6): zweite, kleinere Zeile unter dem Eintrag (Beschreibung, nicht Teil des Namens).
  zusatz?: string;
  to?: string;
  href?: string;
  extern?: boolean;
  aktion?: MenueAktion;
  unter?: MenueEintrag[];
}

// Nur das Ebenenobjekt des Servers entscheidet über Block 2, nie ein Rollenname; die Sicht verändert das Menü nicht.
// „KI-Cockpit“ (F-04, bis dahin „Nutzung im Team“) nur mit ebenen.kiCockpit, das der Server genau mit teamNutzung setzt (Ä-20,
// UK-15): die Buchhaltung (Einstellungen der Firma ohne „Personen ansehen“) bekommt den Eintrag nicht. Ziel vom Server wie bei
// der Plattform-Verwaltung (PF-105); teamNutzung allein zeigt den Eintrag nicht mehr. „Plattform-Verwaltung“ nur mit
// ebenen.verwaltung, Ziel vom Server.
// rechtliches und produkt: seit PF-104 ungenutzt, die Rechtslinks und „Über …“ stehen auf /hilfe/dokumente (Dokumente.tsx); sie
// bleiben in der Signatur, damit bestehende Aufrufer unverändert bleiben.
// F-23 (PF-NB-verwaltung-6; Betreiber H-01, Persona A2): wer Plattformbereiche hat (ebenen.verwaltung), liest unter „KI-Cockpit“
// und „Einstellungen der Firma“ den Namen der eigenen Firma; „Plattform-Verwaltung“ steht als eigener Block (4) mit Zusatzzeile
// vor „Abmelden“. Ohne ebenen.verwaltung bleibt das Menü wie bisher. F-22 und F-04 bleiben: Weg und Wort unverändert.
export function menueEintraege({ ebenen }: { ebenen?: Ebenen | null; sicht?: string | null; produkt: string; rechtliches?: Rechtslink[] }): MenueEintrag[] {
  const firma = ebenen?.verwaltung && ebenen.adminEinstellungen?.firmaName ? { zusatz: ebenen.adminEinstellungen.firmaName } : {};
  return [
    { key: 'einstellungen', label: EBENEN.meineEinstellungen, to: EINSTELLUNGEN_PFAD, block: 1 },
    // PF-104 (F-06, F-19; BAU-MENUE.md, BAU-HILFE.md 1.2): seit PF-104 ein Link auf die Hilfe-Seite; /hilfe leitet auf
    // /hilfe/dokumente. Das Untermenü (Plan 1a Welle 1 G3-3 und F-01) entfällt, „Fehler melden“ heißt dort „Neue Anfrage“.
    { key: 'hilfe', label: MENUE.hilfeUndFeedback, block: 1, to: HILFE_PFAD },
    ...(ebenen?.kiCockpit ? [{ key: 'ki-cockpit', label: MENUE.kiCockpit, to: ebenen.kiCockpit.ziel, block: 2 as const, ...firma }] : []),
    ...(ebenen?.adminEinstellungen ? [{ key: 'admin-einstellungen', label: EBENEN.adminEinstellungen, to: ORGANISATION_PFAD, block: 2 as const, ...firma }] : []),
    ...(ebenen?.verwaltung ? [{ key: 'verwaltung', label: MENUE.plattformVerwaltung, to: ebenen.verwaltung.ziel, block: 4 as const,
      zusatz: MENUE.plattformVerwaltungZusatz }] : []),
    { key: 'abmelden', label: MENUE.abmelden, aktion: 'abmelden', block: 3 },
  ];
}
